import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DrilldownDataExportResult } from '@databox/ember-integrations/lib/components/Datasets/DrilldownDialog/DrilldownDialog';
import DrilldownDialogEntryPoint, {
	type DrilldownDialogEntryPointProps,
} from '@databox/ember-integrations/lib/components/Datasets/DrilldownDialog/DrilldownDialogEntryPoint';
import type { Root } from '@databox/ember-integrations/lib/components/Setup';
import { create } from '@databox/ember-integrations/lib/components/Setup';

import type { PopupData, ReactIntegrations, Service } from 'webapp-types';
import { BillingFeature, TrackingRawDataFeature } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import CommonPopupsDrawerComponent from 'webapp/components/common/popups/drawer';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

export default class RawDataPopupComponent extends PopupInnerModalComponent<PopupData.RawData.Popup> {
	@service('popup') declare popupService: Service.Popup;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('metric-raw-data') declare metricRawDataService: Service.MetricRawData;
	@service('react') declare reactService: Service.React;
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('dataset-events') declare datasetsEventsService: Service.DatasetEvents;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;

	reactElement: ReactIntegrations.Root = null;
	@tracked isLoading = false;
	@tracked period?: [number, number];

	get drawerArgs() {
		return {
			title: this.popupData.metricName,
			onClose: this.close,
		};
	}

	get source() {
		return this.popupData.source;
	}

	get exportRequest() {
		return this.popupData.exportRequest;
	}

	@action
	setupView(node: HTMLElement): void {
		this.reactElement = create(node as HTMLDivElement);
		this.renderRawDataView();

		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderRawDataView);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderRawDataView);
	}

	@action
	exportRawData() {
		if (this.isLoading) return;

		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		this.isLoading = true;

		this.analyticsService.event(TrackingEvent.METRIC_DATA_RAW_EXPORT, {
			feature: TrackingRawDataFeature.DATABOARD,
			origin: 'popup',
		});

		this.exportRequest(this.period)
			.then(() => {
				this.datasetsEventsService.handleDataExport(
					this.popupData.metricName,
					this.popupData.source.numericId!,
				);
			})
			.catch((error) => {
				this.isLoading = false;
				this.requestService.handleGenericThrownError(error);
			});
	}

	private exportRawDataHandler = (result: DrilldownDataExportResult) => {
		this.isLoading = false;
		const { name, spaceAccessId, url, fileSize, fileType } = result;
		this.datasetsEventsService.handleDataExport(name, spaceAccessId, url, fileSize, fileType);
	};

	@action
	periodChangeHandler(dates: [Date | null, Date | null]) {
		if (dates[0] && dates[1]) {
			this.period = [dates[0].getTime() / 1000, dates[1].getTime() / 1000];
		}
	}

	renderRawDataView() {
		const props: DrilldownDialogEntryPointProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			request: this.popupData.request,
			filteredBy: this.popupData.filteredBy,
			period: this.popupData.period,
			onDataExport: this.exportRawDataHandler,
			onPeriodChange: this.periodChangeHandler,
		};

		this.reactService.render(
			this.reactElement as Root,
			DrilldownDialogEntryPoint(props),
			getShadowDomMode(this.accountService.email),
		);
	}

	override willDestroy() {
		super.willDestroy();

		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}
	<template>
		<CommonPopupsDrawerComponent @icon="Table" @onClose={{this.drawerArgs.onClose}}>
			<:header>
				<div class="text">View raw data for "{{this.popupData.metricName}}"</div>
				<div class="connection">
					(
					<img src={{this.source.sourceType.avatar}} alt={{this.source.title}} />
					{{this.source.title}}
					)
				</div>

				<a
					class="export-data {{if this.isLoading 'loading disabled'}}"
					{{on "click" this.exportRawData}}
				>
					{{#if this.isLoading}}
						<span class="loading"></span>
						<span class="text">Exporting data...</span>
					{{else}}
						<CommonUiIconComponent @name="Download" @size="sm" />
						<span class="text">Export data</span>
						<span class="new">New</span>
					{{/if}}
				</a>
			</:header>
			<:content>
				<div id="page-raw-data" {{didInsert this.setupView}} />
			</:content>

		</CommonPopupsDrawerComponent>
	</template>
}
